import { useTranslation } from 'react-i18next';
import type { QuerySpec } from '@/types/query';
import { useQueryResult } from '@/hooks/useQueryResult';
import { summarizeCompareKpi } from '@/engine/pivots/compare';
import { WidgetContainer } from './WidgetContainer';
import { ReloadButton } from './ReloadButton';

type KpiCardProps = {
  title: string;
  spec: QuerySpec;
  /** Format function for the metric value. Defaults to toLocaleString. */
  format?: (value: number) => string;
  /** When provided, a × button in the header calls this. */
  onRemove?: (() => void) | undefined;
  /** When provided, a gear button in the header calls this. */
  onSettings?: (() => void) | undefined;
  /** Optional note shown below the value. */
  note?: string | undefined;
};

/**
 * Single-metric display: big number with optional label.
 * Expects the query to return exactly one row with one metric.
 *
 * Compare mode (D-020): renders period A's value as the headline plus
 * a delta line ("Δ +200 · +25% vs Mart 2024"). Up/down arrow + green/red
 * coloring follows the delta sign; magnitude only suppresses % when
 * period B is zero.
 */
export function KpiCard({
  title,
  spec,
  format = (v) => v.toLocaleString(),
  onRemove,
  onSettings,
  note,
}: KpiCardProps): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, error, reload } = useQueryResult(spec);
  const metricAlias = spec.metrics[0]?.alias ?? 'value';
  const compare = data?.compareMeta;

  const compareSummary = compare
    ? summarizeCompareKpi(data!.rows, metricAlias)
    : null;

  const raw = data?.rows[0]?.[metricAlias];
  const fallbackValue = typeof raw === 'number' ? raw : Number(raw);

  const headline = compareSummary
    ? compareSummary.valueA
    : Number.isFinite(fallbackValue)
      ? fallbackValue
      : null;

  const deltaColor =
    compareSummary?.delta == null
      ? 'var(--color-muted)'
      : compareSummary.delta > 0
        ? 'var(--color-success, #16a34a)'
        : compareSummary.delta < 0
          ? 'var(--color-danger)'
          : 'var(--color-muted)';
  const arrow =
    compareSummary?.delta == null
      ? ''
      : compareSummary.delta > 0
        ? '▲'
        : compareSummary.delta < 0
          ? '▼'
          : '·';

  return (
    <WidgetContainer
      title={title}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      onSettings={onSettings}
      note={note}
      titleBarExtra={<ReloadButton onReload={reload} disabled={isLoading} />}
    >
      <div className="flex flex-col items-center justify-center h-full gap-1 p-4">
        {data ? (
          compare && compareSummary ? (
            <CompareKpiBody
              labelA={compare.labelA}
              labelB={compare.labelB}
              valueA={compareSummary.valueA}
              valueB={compareSummary.valueB}
              delta={compareSummary.delta}
              percent={compareSummary.percent}
              deltaColor={deltaColor}
              arrow={arrow}
              format={format}
            />
          ) : (
            <span
              className="text-3xl font-bold tabular-nums"
              style={{ color: 'var(--color-fg)' }}
            >
              {headline != null && Number.isFinite(headline)
                ? format(headline)
                : '—'}
            </span>
          )
        ) : (
          <span
            className="text-sm"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('common.noData')}
          </span>
        )}
      </div>
    </WidgetContainer>
  );
}

// ---------------------------------------------------------------------------
// Compare-mode body
// ---------------------------------------------------------------------------

type CompareKpiBodyProps = {
  labelA: string;
  labelB: string;
  valueA: number | null;
  valueB: number | null;
  delta: number | null;
  percent: number | null;
  /** Sign-encoded colour for the delta badge (success / danger / muted). */
  deltaColor: string;
  /** Pre-resolved arrow character. */
  arrow: string;
  format: (value: number) => string;
};

/**
 * Compare-mode KPI layout. Three-tier vertical hierarchy:
 *
 *   1. Period A value                            (headline)
 *   2. "Mevcut dönem: <labelA>"                  (small muted caption)
 *   3. "Değişim: <±delta> | <±percent>"          (sign-coloured)
 *
 * The headline carries the absolute number for the active period; the
 * change line carries both the absolute delta and the percent so the
 * user sees magnitude and proportion in one row. Sign is encoded in
 * `deltaColor` (green / red / muted) and `arrow` (▲ / ▼).
 */
function CompareKpiBody({
  labelA,
  labelB: _labelB,
  valueA,
  valueB: _valueB,
  delta,
  percent,
  deltaColor,
  arrow,
  format,
}: CompareKpiBodyProps): JSX.Element {
  const { t } = useTranslation();
  const headline = valueA != null && Number.isFinite(valueA) ? format(valueA) : '—';
  const deltaText =
    delta == null
      ? '—'
      : `${delta >= 0 ? '+' : ''}${format(delta)}`;
  const percentText =
    percent == null
      ? '—'
      : `${percent >= 0 ? '+' : ''}${(percent * 100).toFixed(1)}%`;

  return (
    <div className="flex flex-col items-center text-center gap-1">
      <span
        className="text-3xl font-bold tabular-nums leading-tight"
        style={{ color: 'var(--color-fg)' }}
      >
        {headline}
      </span>
      <span
        className="text-[11px] tabular-nums mt-1"
        style={{ color: 'var(--color-muted)' }}
      >
        {t('compare.kpi.currentPeriod')}:{' '}
        <span style={{ color: 'var(--color-fg)' }} className="font-medium">
          {labelA}
        </span>
      </span>
      <span
        className="inline-flex items-baseline gap-1 text-xs font-semibold tabular-nums"
        style={{ color: deltaColor }}
        aria-label={
          percent == null
            ? `${t('compare.kpi.change')} —`
            : `${t('compare.kpi.change')} ${deltaText} ${percentText}`
        }
      >
        <span style={{ color: 'var(--color-muted)' }} className="font-normal">
          {t('compare.kpi.change')}:
        </span>
        <span aria-hidden>{arrow}</span>
        <span>{deltaText}</span>
        <span style={{ color: 'var(--color-muted)' }} className="font-normal">|</span>
        <span>{percentText}</span>
      </span>
    </div>
  );
}
